import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logIn, resetPassword } from '../../firebase/authService';
import { useAuth } from '../../contexts/AuthContext';
import { TruckIntro } from '../../components/common/truck';
import { ReactComponent as TippiLogoDark } from '../../assets/logo-dark.svg';
import { COLORS, FONTS, SHADOWS } from '../../styles/tokens';

const ERROR_MESSAGES = {
  'auth/invalid-credential': 'Feil e-post eller passord.',
  'auth/user-not-found':     'Ingen konto med denne e-posten.',
  'auth/wrong-password':     'Feil passord.',
  'auth/too-many-requests':  'For mange forsøk. Vent litt og prøv igjen.',
};

const RESET_ERROR_MESSAGES = {
  'auth/invalid-email':      'Ugyldig e-postadresse.',
  'auth/too-many-requests':  'For mange forsøk. Vent litt og prøv igjen.',
};

export default function Login() {
  const [mode, setMode]         = useState('login'); // 'login' | 'reset'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const [resetEmail, setResetEmail]     = useState('');
  const [resetError, setResetError]     = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent]       = useState(false);

  const navigate = useNavigate();
  const { currentUser, effectiveRole } = useAuth();

  useEffect(() => {
    if (currentUser && effectiveRole) navigate('/');
  }, [currentUser, effectiveRole, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await logIn(email, password);
    } catch (err) {
      setError(ERROR_MESSAGES[err.code] ?? 'Innlogging feilet. Prøv igjen.');
      setLoading(false);
    }
  }

  function switchToReset() {
    setMode('reset');
    setError('');
    setResetError('');
    setResetSent(false);
    setResetEmail(email);
  }

  function switchToLogin() {
    setMode('login');
    setResetError('');
  }

  async function handleResetSubmit(e) {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetSent(true);
    } catch (err) {
      // auth/user-not-found vises bevisst IKKE — det ville avslørt hvilke
      // e-poster som er registrert. Andre feil (ugyldig e-post, rate-limit)
      // vises som normalt.
      if (err.code === 'auth/user-not-found') {
        setResetSent(true);
      } else {
        setResetError(RESET_ERROR_MESSAGES[err.code] ?? 'Kunne ikke sende e-post. Prøv igjen.');
      }
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      {/* Left brand panel — desktop only */}
      <div className="tippi-brand-panel">
        <div style={{ marginBottom: 32 }}>
          <TruckIntro size={112} theme="dark" />
        </div>
        <h1 style={styles.brandTitle}>Tippi</h1>
        <p style={styles.brandTagline}>
          Norges markedsplass for<br />løsmasse og lastebiltransport
        </p>
        <div style={styles.brandFeatures}>
          {[
            'Sanntids oppdragsmarked',
            'Forseglet anbudssystem',
            'Automatisk fakturering',
          ].map(f => (
            <div key={f} style={styles.brandFeatureItem}>
              <span style={styles.brandFeatureDot} />
              {f}
            </div>
          ))}
        </div>
        <div style={styles.brandAccentBar} />
      </div>

      {/* Right form panel */}
      <div style={styles.formPanel}>
        <div style={styles.card}>
          {/* Mobile-only logo */}
          <div className="tippi-mobile-logo">
            <TippiLogoDark style={{ width: 44, height: 'auto' }} />
            <span style={styles.mobileLogoText}>Tippi</span>
          </div>

          {mode === 'login' ? (
            <>
              <h2 style={styles.heading}>Logg inn</h2>

              {error && <div style={styles.error}>{error}</div>}

              <form onSubmit={handleSubmit} style={styles.form}>
                <label style={styles.label}>E-post</label>
                <input
                  className="tippi-input"
                  style={styles.input}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  autoFocus
                  placeholder="din@epost.no"
                />

                <div style={styles.passwordRow}>
                  <label style={{ ...styles.label, marginTop: 0 }}>Passord</label>
                  <button type="button" style={styles.forgotLink} onClick={switchToReset}>
                    Glemt passord?
                  </button>
                </div>
                <input
                  className="tippi-input"
                  style={styles.input}
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                />

                <button className="tippi-btn" style={styles.btn} type="submit" disabled={loading}>
                  {loading ? 'Logger inn…' : 'Logg inn'}
                </button>
              </form>

              <p style={styles.footer}>
                Ny bruker? <Link to="/register" style={styles.link}>Registrer deg</Link>
              </p>
              {process.env.REACT_APP_SHOW_CARRIER_LINK === 'true' && (
                <>
                  <p style={styles.footer}>
                    Sjåfør eller ansatt i et transportfirma? <Link to="/login/telefon" style={styles.link}>Logg inn med mobil</Link>
                  </p>
                  <p style={styles.footer}>
                    <Link to="/register/transportor" style={styles.link}>Registrer transportfirma</Link>
                  </p>
                </>
              )}
            </>
          ) : (
            <>
              <h2 style={styles.heading}>Glemt passord</h2>

              {resetSent ? (
                <>
                  <div style={styles.success}>
                    Hvis det finnes en konto med denne e-posten, har vi sendt en lenke for å tilbakestille passordet.
                  </div>
                  <button type="button" style={styles.backLink} onClick={switchToLogin}>
                    ← Tilbake til innlogging
                  </button>
                </>
              ) : (
                <>
                  <p style={styles.resetIntro}>
                    Skriv inn e-posten din, så sender vi deg en lenke for å sette nytt passord.
                  </p>

                  {resetError && <div style={styles.error}>{resetError}</div>}

                  <form onSubmit={handleResetSubmit} style={styles.form}>
                    <label style={styles.label}>E-post</label>
                    <input
                      className="tippi-input"
                      style={styles.input}
                      type="email"
                      value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      autoComplete="email"
                      required
                      autoFocus
                      placeholder="din@epost.no"
                    />

                    <button className="tippi-btn" style={styles.btn} type="submit" disabled={resetLoading}>
                      {resetLoading ? 'Sender…' : 'Send tilbakestillingslenke'}
                    </button>
                  </form>

                  <button type="button" style={styles.backLink} onClick={switchToLogin}>
                    ← Tilbake til innlogging
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', display: 'flex', background: COLORS.bg,
  },
  brandTitle: {
    fontFamily: FONTS.display, fontSize: 52, fontWeight: 700,
    color: '#fff', margin: '0 0 14px', letterSpacing: 1,
  },
  brandTagline: {
    fontSize: 17, color: 'rgba(255,255,255,0.68)',
    margin: '0 0 40px', lineHeight: 1.6,
  },
  brandFeatures: {
    display: 'flex', flexDirection: 'column', gap: 16,
  },
  brandFeatureItem: {
    display: 'flex', alignItems: 'center', gap: 13,
    color: 'rgba(255,255,255,0.82)', fontSize: 15, fontWeight: 500,
  },
  brandFeatureDot: {
    width: 7, height: 7, borderRadius: '50%',
    background: COLORS.accent, flexShrink: 0,
  },
  brandAccentBar: {
    marginTop: 56, width: 40, height: 4,
    background: COLORS.accent, borderRadius: 2,
  },
  formPanel: {
    flex: 1, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '40px 20px',
  },
  card: {
    background: COLORS.white, borderRadius: 16, padding: '48px 40px',
    width: '100%', maxWidth: 420,
    boxShadow: SHADOWS.md,
  },
  mobileLogoText: {
    fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, color: COLORS.textDark,
  },
  heading: {
    fontFamily: FONTS.display, fontSize: 30, fontWeight: 700,
    color: COLORS.textDark, margin: '0 0 28px', letterSpacing: 0.3,
  },
  form: {
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  label: {
    fontSize: 13, fontWeight: 600, color: COLORS.textMid, marginTop: 10,
  },
  passwordRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10,
  },
  forgotLink: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, color: COLORS.primary,
  },
  backLink: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontSize: 14, fontWeight: 600, color: COLORS.primary, marginTop: 20,
    display: 'block', textAlign: 'center', width: '100%',
  },
  resetIntro: {
    fontSize: 14, color: COLORS.textMid, margin: '0 0 20px', lineHeight: 1.5,
  },
  success: {
    background: COLORS.successBg, color: COLORS.successText,
    padding: '12px 14px', borderRadius: 8,
    fontSize: 14, lineHeight: 1.5,
  },
  input: {
    padding: '12px 14px', borderRadius: 8,
    border: `1.5px solid ${COLORS.border}`, fontSize: 15,
    width: '100%', transition: 'border-color 0.15s',
  },
  btn: {
    marginTop: 18, padding: '14px', borderRadius: 8,
    border: 'none', background: COLORS.primary,
    color: COLORS.onPrimary, fontSize: 16, fontWeight: 600, cursor: 'pointer', width: '100%',
  },
  error: {
    background: COLORS.dangerBg, color: COLORS.dangerText,
    padding: '12px 14px', borderRadius: 8,
    fontSize: 14, marginBottom: 16, lineHeight: 1.4,
  },
  footer: {
    textAlign: 'center', marginTop: 24, fontSize: 14, color: COLORS.textMid,
  },
  link: {
    color: COLORS.primary, fontWeight: 600,
  },
};
